import type { AppVersionInfo } from '~/utils/appVersion'
import { formatBuiltAtForDisplay } from '~/utils/appVersion'

export type { AppVersionInfo }

export function useAppVersions() {
  const appVersion = ref<AppVersionInfo | null>(null)
  const apiVersion = ref<AppVersionInfo | null>(null)
  const loadingApp = ref(false)
  const loadingApi = ref(false)

  async function fetchAppVersion(): Promise<void> {
    if (!import.meta.client) {
      return
    }
    loadingApp.value = true
    try {
      appVersion.value = await $fetch<AppVersionInfo>('/api/version', { method: 'GET' })
    } catch {
      appVersion.value = null
    } finally {
      loadingApp.value = false
    }
  }

  async function fetchApiVersion(): Promise<void> {
    if (!import.meta.client) {
      return
    }
    loadingApi.value = true
    const runtimeConfig = useRuntimeConfig()
    // Toujours viser l’API Adonis (proxy `/api-monorga`), pas la route Nitro IHM `/api/version`.
    const baseURL = String(runtimeConfig.public.apiBaseUrl ?? '').trim() || '/api-monorga'

    try {
      apiVersion.value = await $fetch<AppVersionInfo>('/version', {
        baseURL,
        method: 'GET',
      })
    } catch {
      apiVersion.value = null
    } finally {
      loadingApi.value = false
    }
  }

  async function loadAllVersions(): Promise<void> {
    await Promise.all([fetchAppVersion(), fetchApiVersion()])
  }

  function formatVersionBuildLabel(info: AppVersionInfo | null, productLabel: string): string | null {
    if (!info?.version) {
      return null
    }
    if (info.build) {
      return `${productLabel} ${info.version} (${info.build})`
    }
    return `${productLabel} ${info.version}`
  }

  const footerLabel = computed(() => {
    const parts: string[] = []
    const appPart = formatVersionBuildLabel(appVersion.value, 'App')
    const apiPart = formatVersionBuildLabel(apiVersion.value, 'API')
    if (appPart) {
      parts.push(appPart)
    }
    if (apiPart) {
      parts.push(apiPart)
    }
    return parts.length > 0 ? parts.join(' · ') : null
  })

  return {
    appVersion,
    apiVersion,
    loadingApp,
    loadingApi,
    loadAllVersions,
    footerLabel,
    formatBuiltAtForDisplay,
  }
}
